<template>
  <div class="therapist-table">
    <p id="title">{{ $t('result-table-title') }}</p>

    <div class="san-row">
      <div class="parbase table">
        <div class="col-md-12">
          <div class="san-table-container">
            <div class="san-table san-table--5cols">
              <div
                style="order: 1"
                class="
                  san-table-cell
                  san-table-cell--head
                  san-table-cell--first
                  san-table-cell--round-corner-top-left
                "
              >
                {{ $t('result-table-last-name-column-title') }}
              </div>
              <div style="order: 2" class="san-table-cell san-table-cell--head">
                {{ $t('result-table-first-name-column-title') }}
              </div>
              <div style="order: 3" class="san-table-cell san-table-cell--head">
                {{ $t('result-table-street-column-title') }}
              </div>
              <div style="order: 4" class="san-table-cell san-table-cell--head">
                {{ $t('result-table-address-column-title') }}
              </div>
              <div
                style="order: 5"
                class="
                  san-table-cell
                  san-table-cell--head
                  san-table-cell--last
                  san-table-cell--round-corner-top-right
                "
              >
                {{ $t('result-table-detail-column-title') }}
              </div>
            </div>

            <template v-for="(therapist, i) in results">
              <div :key="i" class="san-table san-table--5cols">
                <div
                  style="order: 1"
                  class="san-table-cell san-table-cell--first"
                  :class="{ [isFirstInLastRowOfTable(i)]: true }"
                >
                  {{ therapist.officialName }}
                </div>
                <div style="order: 2" class="san-table-cell">
                  {{ therapist.firstName }}
                </div>
                <div style="order: 3" class="san-table-cell">
                  {{ (therapist.commercialAddress || {}).addressLine }}
                </div>
                <div style="order: 4" class="san-table-cell">
                  {{ (therapist.commercialAddress || {}).zipCode }} /
                  {{ (therapist.commercialAddress || {}).city }}
                </div>
                <div
                  style="order: 5"
                  class="san-table-cell san-table-cell--last"
                  :class="{ [isLastInLastRowOfTable(i)]: true }"
                >
                  <p
                    class="detail-link-text"
                    @click="openTherapistPopup(therapist)"
                  >
                    {{ $t('result-table-detail-link-text') }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <sanitas-popup
      class="therapist-popup"
      close-icon
      :show="show"
      @close="show = false"
    >
      <h3 class="popup__title">{{ $t('therapist-popup-title') }}</h3>

      <p class="popup__description">
        {{ $t('therapist-popup-description') }}
      </p>
      <p class="popup__therapist-data">
        {{ selectedTherapist.firstName }}
        {{ selectedTherapist.officialName }}
      </p>
      <p class="popup__therapist-data">
        {{ (selectedTherapist.commercialAddress || {}).addressLine }}
      </p>
      <p class="popup__therapist-data">
        {{ (selectedTherapist.commercialAddress || {}).zipCode }}
        {{ (selectedTherapist.commercialAddress || {}).city }}
      </p>

      <div class="popup__therapist-contact">
        <div class="popup__therapist-contact-labels">
          <template v-if="(selectedTherapist.contact || {}).phone">
            <p class="popup__therapist-data">
              {{ $t('therapist-popup-phone-label') }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).mobile">
            <p class="popup__therapist-data">
              {{ $t('therapist-popup-mobile-label') }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).fax">
            <p class="popup__therapist-data">
              {{ $t('therapist-popup-fax-label') }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).email">
            <p class="popup__therapist-data">
              {{ $t('therapist-popup-email-label') }}
            </p>
          </template>
        </div>

        <div class="popup__therapist-contact-data">
          <template v-if="(selectedTherapist.contact || {}).phone">
            <p class="popup__therapist-data">
              {{ (selectedTherapist.contact || {}).phone }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).mobile">
            <p class="popup__therapist-data">
              {{ (selectedTherapist.contact || {}).mobile }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).fax">
            <p class="popup__therapist-data">
              {{ (selectedTherapist.contact || {}).fax }}
            </p>
          </template>

          <template v-if="(selectedTherapist.contact || {}).email">
            <p class="popup__therapist-data">
              <a :href="`mailto:${(selectedTherapist.contact || {}).email}`">{{
                (selectedTherapist.contact || {}).email
              }}</a>
            </p>
          </template>
        </div>
      </div>

      <dl class="popup__therapy-methods">
        <dt class="first-title">
          {{ $t('therapist-popup-method-title') }}
        </dt>
        <dd class="first-title">
          {{ $t('therapist-popup-certificate-title') }}
        </dd>

        <template v-for="(method, i) in selectedTherapist.methods">
          <dt :key="i">
            {{ method.name }}

            <template v-if="method.comment">
              <sanitas-tooltip
                class="popup__tooltip"
                :content="method.comment"
              ></sanitas-tooltip>
            </template>
          </dt>
          <dd :key="`${i}dd`">{{ method.society }}</dd>
        </template>
      </dl>
    </sanitas-popup>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'

import SanitasPopup from '@/components/popup/SanitasPopup'
import SanitasTooltip from '@/modules/forms/components/SanitasTooltip'

export default {
  name: 'TherapistSearchTable',
  components: {
    SanitasTooltip,
    SanitasPopup,
  },
  props: {
    /**
     * results displayed in the table
     */
    results: {
      type: Array,
      required: true,
    },
  },
  setup() {
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    selectedTherapist: {},
    isTherapistPopupOpen: false,
    show: false,
  }),
  methods: {
    isFirstInLastRowOfTable(index) {
      return index === this.results.length - 1
        ? 'san-table-cell--round-corner-bottom-left'
        : ''
    },

    isLastInLastRowOfTable(index) {
      return index === this.results.length - 1
        ? 'san-table-cell--round-corner-bottom-right'
        : ''
    },
    openTherapistPopup(item) {
      this.selectedTherapist = item
      this.show = true
    },
  },
}
</script>

<style lang="postcss">
.therapist-table {
  margin: 40px 0 20px 0;
  display: block;
  .is-hidden {
    display: none;
  }

  .san-table-container {
    min-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .san-table {
    font-weight: 400;
    font-style: normal;
    font-size: 11px;
    line-height: 1;
    letter-spacing: 0.25px;
    color: var(--c-secondary-neutral-2);
    display: flex;
    flex-wrap: nowrap;
    padding: 0;
    width: 100%;
    min-width: 100%;
    position: relative;
    top: 1px;
    left: 1px;
  }

  .san-table-cell {
    font-weight: 400;
    font-style: normal;
    font-size: 15px;
    line-height: 1.35;
    letter-spacing: 0.25px;
    color: var(--c-primary-neutral-1);
    min-height: 34px;
    padding: 0.5rem;
    flex-grow: 1;
    min-width: 150px;
    overflow: hidden;
    border: 1px solid var(--c-secondary-neutral-3);
    border-right-style: dashed;
    border-left-style: dashed;
    margin: -1px 0 0 -1px;
  }
  .san-table-cell--first {
    border-left-style: solid;
  }

  .san-table-cell--last {
    border-right-style: solid;
  }

  .san-table--5cols > .san-table-cell {
    width: 20%;
  }

  .san-table-cell--head {
    font-weight: 400;
    font-style: normal;
    font-size: 13px;
    line-height: 1.35;
    letter-spacing: 0.5px;
    color: var(--c-primary-neutral-1);
    background-color: var(--c-primary-neutral-2);

    &.san-table-cell {
      border-top: none;
    }
    &.san-table-cell--round-corner-top-left {
      border-left: none;
    }

    &.san-table-cell--round-corner-top-right {
      border-right: none;
    }
  }

  .san-table-cell--round-corner-top-left {
    border-top-left-radius: 10px;
  }

  .san-table-cell--round-corner-top-right {
    border-top-right-radius: 10px;
  }

  .san-table-cell--round-corner-bottom-left {
    border-bottom-left-radius: 10px;
  }

  .san-table-cell--round-corner-bottom-right {
    border-bottom-right-radius: 10px;
  }

  @media (--lg) {
    .san-table-container {
      overflow: inherit;
    }
  }

  #title {
    font-weight: 400;
    font-style: normal;
    font-size: 13px;
    line-height: 1.35;
    letter-spacing: 0.5px;
    color: var(--c-secondary-neutral-2);
    margin: 0 0 45px 0;
  }

  .detail-link-text {
    font-weight: 700;
    font-style: normal;
    font-size: 15px;
    line-height: 1.1;
    letter-spacing: 0.25px;
    color: var(--c-primary-color-3);
    cursor: pointer;
    display: inline;
  }
}

.therapist-popup {
  .popup__title {
    font-family: 'TheMix Light', sans-serif;
    font-weight: 300;
    font-style: normal;
    font-size: 24px;
    line-height: 1.1;
    color: var(--c-primary-color-2);
    margin-bottom: 25px;
  }

  .popup__description {
    font-weight: 700;
    font-style: normal;
    font-size: 15px;
    line-height: 1.35;
    color: var(--c-primary-neutral-1);
    margin-bottom: 15px;
  }

  .popup__therapist-contact {
    display: inline-flex;
    margin-bottom: 35px;

    .popup__therapist-contact-labels {
      margin-right: 20px;
    }
  }

  .popup__therapist-data {
    font-weight: 400;
    font-style: normal;
    font-size: 15px;
    line-height: 1.35;
    letter-spacing: 0.25px;
    color: var(--c-primary-neutral-1);
    margin: 0;

    a {
      font-weight: 700;
      font-style: normal;
      font-size: 15px;
      line-height: 1.1;
      letter-spacing: normal;
      color: var(--c-primary-color-3);
      cursor: pointer;
      display: inline-block;
      text-decoration: none;
    }
  }
  .popup__tooltip {
    margin-left: 10px;
    display: flex;
  }

  .popup__therapy-methods {
    display: flex;
    flex-wrap: wrap;

    dt,
    dd {
      font-weight: 400;
      font-style: normal;
      font-size: 15px;
      line-height: 1.35;
      letter-spacing: 0.25px;
      color: var(--c-primary-neutral-1);

      display: flex;
      align-items: center;
      margin: 0;
      padding: 13px 0 5px 0;
      width: 50%;
      border-bottom: 1px dotted var(--c-secondary-neutral-3);
    }
    .first-title {
      font-weight: 700;
      font-style: normal;
      font-size: 15px;
      line-height: 1.35;
      color: var(--c-primary-neutral-1);
    }
    dd {
      padding-left: 20px;
      width: calc(50% - 20px);
    }
  }
}
</style>
